import { Component, OnInit, OnChanges, Input, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { CalendarEvent } from '../../utils/CalendarEvent';
import { Calendar } from '../../utils/Calendar';
import { WeekDay } from '../../utils/week-day';
import { DayViewHour } from '../../utils/day/DayViewHour';
import { Subject } from 'rxjs/Subject';
import { Subscription } from 'rxjs/Subscription';
import { CalendarUtilsService } from '../../utils/calendar-utils.service';
import * as dateFns from 'date-fns';
export class WeekViewComponent implements OnInit, OnChanges {
/**
 * The current view date
 */
viewDate: Date;
/**
 * An array of events to display on view
 */
events: CalendarEvent[] = [];
/**
 * An array of calendars to display on view.
 */
calendars: Calendar[] = [];
/**
 * The start number of the week
 */
weekStartsOn: number;
/**
 * An array of day indexes (0 = sunday, 1 = monday etc) that will be hidden on the view
 */
excludeDays: number[] = [];
/**
 * An array of day indexes (0 = sunday, 1 = monday etc) that indicate which days are weekends
 */
weekendDays: number[];
/**
 * @hidden
 */
days: WeekDay[];
/**
 * @hidden
 */
hours: DayViewHour[] = [];
/**
 * An observable that when emitted on will re-render the current view
 */
refresh: Subject<any>;
/**
 * The precision to display events.
 * `days` will round event start and end dates to the nearest day and `minutes` will not do this rounding
 */
precision: 'days' | 'minutes' = 'days';
/**
 * The number of segments in an hour. Must be <= 6
 */
hourSegments = 2;
/**
 * The height in pixels of each hour segment
 */
hourSegmentHeight = 30;
/**
 * The day start hours in 24 hour time. Must be 0-23
 */
dayStartHour = 0;
/**
 * The day start minutes. Must be 0-59
 */
dayStartMinute = 0;
/**
 * The day end hours in 24 hour time. Must be 0-23
 */
dayEndHour = 23;
/**
 * The day end minutes. Must be 0-59
 */
dayEndMinute = 59;
/**
 * The width in pixels of each event on the view
 */
eventWidth = 150;
/**
 * An output that will be called before the view is rendered for the current week.
 * If you add the `cssClass` property to a day in the header it will add that class to the cell element in the template
 */
beforeViewRender: EventEmitter<{ header: WeekDay[], body: DayViewHour[] }> = new EventEmitter();
/**
 * Called when the event title is clicked
 */
eventClicked: EventEmitter<{ event: CalendarEvent }> = new EventEmitter<{
    event: CalendarEvent;
  }>();
/**
 * @hidden
 */
view: any;
/**
 * @hidden
 */
refreshSubscription: Subscription;

  currentTime = this.getCurrentTime();
/**
 * @param {?} cdr
 * @param {?} utils
 */
constructor(private cdr: ChangeDetectorRef,
private utils: CalendarUtilsService) { }
/**
 * @return {?}
 */
ngOnInit() {
    if (this.refresh) {
      this.refreshSubscription = this.refresh.subscribe(() => {
        this.refreshAll();
        this.cdr.markForCheck();
      });
    }
  }
/**
 * @hidden
 * @param {?} changes
 * @return {?}
 */
ngOnChanges(changes: any): void {
    if (changes.viewDate || changes.excludeDays || changes.weekendDays) {
      this.refreshHeader();
    }
    if (changes.events || changes.calendars || changes.viewDate || changes.excludeDays) {
       this.refreshBody();
    }
  }
/**
 * @return {?}
 */
public getCurrentTime() {
    const /** @type {?} */ time = new Date();
    const /** @type {?} */ top = dateFns.differenceInMinutes(time, dateFns.startOfDay(time));
    return top;
  }
/**
 * @return {?}
 */
private refreshBody(): void {
    this.view = this.utils.getWeekView({
      events: this.events,
      calendars: this.calendars,
      viewDate: this.viewDate,
      hourSegments: this.hourSegments,
      dayStart: {
        hour: this.dayStartHour,
        minute: this.dayStartMinute
      },
      dayEnd: {
        hour: this.dayEndHour,
        minute: this.dayEndMinute
      },
      segmentHeight: this.hourSegmentHeight,
      weekStartsOn: this.weekStartsOn,
    });
  }
/**
 * @return {?}
 */
private refreshHeader(): void {
    this.days = this.utils.getWeekViewHeader({
      viewDate: this.viewDate,
      weekStartsOn: this.weekStartsOn,
      excluded: this.excludeDays,
      weekendDays: this.weekendDays
    });
    this.hours = this.utils.getDayViewHourGrid({
      viewDate: this.viewDate,
      hourSegments: this.hourSegments,
      dayStart: {
        hour: this.dayStartHour,
        minute: this.dayStartMinute
      },
      dayEnd: {
        hour: this.dayEndHour,
        minute: this.dayEndMinute
      }
    });
    this.beforeViewRender.emit({
      header: this.days,
      body: this.hours
    });
  }
/**
 * @return {?}
 */
refreshAll() {
    this.refreshHeader();
    this.refreshBody();
  }

static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  selector: 'calendar-week-view',
  template: `
    <div class="calendar-week-view">
        <calendar-week-view-header
        [days]="days"
        (eventClicked)="eventClicked.emit({event : $event.event})"
        [allDayEvents]="view?.allDays">
        </calendar-week-view-header>
        <div class="cal-week-container">
            <div class="hour-segments">
                <div class="hour-segment-col" *ngFor="let hour of hours">
                  {{ hour.segments[0].date | date: 'hh a' }}
                </div>
            </div>
            <div class="days-container">
                <div
                *ngFor="let day of view.days"
                [ngClass]="{'isToday': day.isToday }"
                class="day">
                <div *ngIf="day.isToday"
                [style.top.px]="currentTime"
                 class="currentTime"></div>
                <div
                *ngFor="let hour of day.hours" 
                class="day-hour"></div>
                <div
                *ngFor="let event of day.events"
                >
                    <div 
                    *ngIf="!event.event.allDay"
                    [style.top.px]="event.top"
                    [style.height.px]="event.height"
                    (click)="eventClicked.emit({event : event.event})"
                    [style.backgroundColor]="event.event.color ? event.event.color : event.event.calendar.color"
                    class="day-event">
                        {{ event.event.title }}
                    </div>
                </div>
                </div>
            </div>
        </div>
    </div>
  `,
  styles: [`
    .calendar-week-view {
      background-color: transparent;
    }

    .cal-week-container{
      display: -webkit-box;
      display: -ms-flexbox;
      display: flex;
      background-color: white;
    }

    .hour-segments{
        height: 1440px;
        display: -webkit-box;
        display: -ms-flexbox;
        display: flex;
        -webkit-box-orient: vertical;
        -webkit-box-direction: normal;
            -ms-flex-direction: column;
                flex-direction: column;
        width: 50px;
        border-right: 1px solid #dddddd;
        border-left: 1px solid #dddddd;
    }
  
    .hour-segment-col:first-child{
      border-top: 1px solid #dddddd;
    }
    .hour-segment-col{
        -webkit-box-flex: 1;
            -ms-flex: 1;
                flex: 1;
        width: 48px;
        border-bottom: 1px solid #dddddd;
        background-color: white;
        font-size: 12px;
        display: -webkit-box;
        display: -ms-flexbox;
        display: flex;
        -webkit-box-align: start;
            -ms-flex-align: start;
                align-items: flex-start;
        -webkit-box-pack: end;
            -ms-flex-pack: end;
                justify-content: flex-end;
        padding-right: 5px;
        color: #555555;
    }
    .days-container {
      -webkit-box-flex: 1;
          -ms-flex: 1;
              flex: 1;
      display: -webkit-box;
      display: -ms-flexbox;
      display: flex;
      height: 1440px;
    }

    .day-hour {
      -webkit-box-flex: 1;
          -ms-flex: 1;
              flex: 1;
      border-bottom: 1px solid #dddddd;
    }
    .day {
      -webkit-box-flex: 1;
          -ms-flex: 1;
              flex: 1;
      border-right: 1px solid #dddddd;
      height: 1440px;
      position: relative;
      display: -webkit-box;
      display: -ms-flexbox;
      display: flex;
      -webkit-box-orient: vertical;
      -webkit-box-direction: normal;
          -ms-flex-direction: column;
              flex-direction: column; 
    }
    .isToday {
      background-color: #fafafa;
    }
    .day-event{
      position: absolute;
      width: 100%;
      color:white;
      font-size: 12px;
      padding: 5px;
      cursor: pointer;
    }

    .currentTime {
      height: 2px;
      width: 100%;
      background-color: #DB4437;
      position: absolute;
    }
  `]
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ChangeDetectorRef, },
{type: CalendarUtilsService, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'viewDate': [{ type: Input },],
'events': [{ type: Input },],
'calendars': [{ type: Input },],
'weekStartsOn': [{ type: Input },],
'excludeDays': [{ type: Input },],
'weekendDays': [{ type: Input },],
'refresh': [{ type: Input },],
'precision': [{ type: Input },],
'hourSegments': [{ type: Input },],
'hourSegmentHeight': [{ type: Input },],
'dayStartHour': [{ type: Input },],
'dayStartMinute': [{ type: Input },],
'dayEndHour': [{ type: Input },],
'dayEndMinute': [{ type: Input },],
'eventWidth': [{ type: Input },],
'beforeViewRender': [{ type: Output },],
'eventClicked': [{ type: Output },],
};
}

function WeekViewComponent_tsickle_Closure_declarations() {
/** @type {?} */
WeekViewComponent.decorators;
/**
 * @nocollapse
 * @type {?}
 */
WeekViewComponent.ctorParameters;
/** @type {?} */
WeekViewComponent.propDecorators;
/**
 * The current view date
 * @type {?}
 */
WeekViewComponent.prototype.viewDate;
/**
 * An array of events to display on view
 * @type {?}
 */
WeekViewComponent.prototype.events;
/**
 * An array of calendars to display on view.
 * @type {?}
 */
WeekViewComponent.prototype.calendars;
/**
 * The start number of the week
 * @type {?}
 */
WeekViewComponent.prototype.weekStartsOn;
/**
 * An array of day indexes (0 = sunday, 1 = monday etc) that will be hidden on the view
 * @type {?}
 */
WeekViewComponent.prototype.excludeDays;
/**
 * An array of day indexes (0 = sunday, 1 = monday etc) that indicate which days are weekends
 * @type {?}
 */
WeekViewComponent.prototype.weekendDays;
/**
 * @hidden
 * @type {?}
 */
WeekViewComponent.prototype.days;
/**
 * @hidden
 * @type {?}
 */
WeekViewComponent.prototype.hours;
/**
 * An observable that when emitted on will re-render the current view
 * @type {?}
 */
WeekViewComponent.prototype.refresh;
/**
 * The precision to display events.
 * `days` will round event start and end dates to the nearest day and `minutes` will not do this rounding
 * @type {?}
 */
WeekViewComponent.prototype.precision;
/**
 * The number of segments in an hour. Must be <= 6
 * @type {?}
 */
WeekViewComponent.prototype.hourSegments;
/**
 * The height in pixels of each hour segment
 * @type {?}
 */
WeekViewComponent.prototype.hourSegmentHeight;
/**
 * The day start hours in 24 hour time. Must be 0-23
 * @type {?}
 */
WeekViewComponent.prototype.dayStartHour;
/**
 * The day start minutes. Must be 0-59
 * @type {?}
 */
WeekViewComponent.prototype.dayStartMinute;
/**
 * The day end hours in 24 hour time. Must be 0-23
 * @type {?}
 */
WeekViewComponent.prototype.dayEndHour;
/**
 * The day end minutes. Must be 0-59
 * @type {?}
 */
WeekViewComponent.prototype.dayEndMinute;
/**
 * The width in pixels of each event on the view
 * @type {?}
 */
WeekViewComponent.prototype.eventWidth;
/**
 * An output that will be called before the view is rendered for the current week.
 * If you add the `cssClass` property to a day in the header it will add that class to the cell element in the template
 * @type {?}
 */
WeekViewComponent.prototype.beforeViewRender;
/**
 * Called when the event title is clicked
 * @type {?}
 */
WeekViewComponent.prototype.eventClicked;
/**
 * @hidden
 * @type {?}
 */
WeekViewComponent.prototype.view;
/**
 * @hidden
 * @type {?}
 */
WeekViewComponent.prototype.refreshSubscription;
/** @type {?} */
WeekViewComponent.prototype.currentTime;
/** @type {?} */
WeekViewComponent.prototype.cdr;
/** @type {?} */
WeekViewComponent.prototype.utils;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
